import { Component, OnDestroy } from '@angular/core';
import { PageWrapperComponent } from 'app/components/ui-kit/page-components/page-wrapper/page-wrapper.component';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'app/services/user.service';
import { AuthService } from 'app/services/auth.service';
import { filter, finalize, take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from 'app/components/ui-kit/loaders/spinner/spinner.component';
import { FormsModule } from '@angular/forms';
import { NgOtpInputComponent } from 'ng-otp-input';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { AlertMessageComponent } from 'app/components/ui-kit/alerts/alert-message/alert-message.component';
import { HeavyHeaderComponent } from 'app/components/ui-kit/typography/heavy-header/heavy-header.component';
import { PhoneInputComponent, PhoneValue } from 'app/components/ui-kit/inputs/phone-input/phone-input.component';

@Component({
  selector: 'app-verify-phone',
  standalone: true,
  imports: [
    PageWrapperComponent,
    HeavyHeaderComponent,
    CommonModule,
    SpinnerComponent,
    FormsModule,
    NgOtpInputComponent,
    PrimaryBtnComponent,
    AlertMessageComponent,
    PhoneInputComponent
  ],
  templateUrl: './verify-phone.component.html',
  styleUrls: ['./verify-phone.component.scss']
})
export class VerifyPhoneComponent implements OnDestroy {
  userId: any;
  isUpdatingUserAccount: boolean = false;

  phoneValue: PhoneValue | string | null = null;
  isPhoneFormatValid: boolean = false;
  isVerifyingPhone: boolean = false;
  verificationError: boolean = false;
  isPhoneVerified: boolean = false;
  phoneNumber: string | undefined;
  formattedSystemPhoneNumber: string | undefined;
  isPhoneNumberUnique: boolean = true;
  isSuspectedVoip: boolean = false;
  voipAttemptCount: number = 0;
  isSendingCode: boolean = false;
  isCodeSent: boolean = false;
  verificationCode: string | undefined;
  isVerifyingCode: boolean = false;
  verificationAttempts: number = 0;
  verifyErrorText: string = '';
  resendCooldownSeconds: number = 0;
  private resendTimer?: ReturnType<typeof setInterval>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private userService: UserService,
  ) { }

  ngOnInit(): void {
    this.authService.user$
      .pipe(
        filter(user => !!user),
        take(1)
      )
      .subscribe(user => {
        this.userId = user.uid;
        // Use it safely here
      });
  }

  onE164Change(e164: string | null): void {
    // e164 is +1XXXXXXXXXX or null
    this.verificationError = false;
    this.verifyErrorText = '';

    if (!e164) {
      this.isPhoneFormatValid = false;
      this.phoneNumber = undefined;
      this.formattedSystemPhoneNumber = undefined;
      return;
    } else {
      this.isPhoneFormatValid = true;
    }
    this.formattedSystemPhoneNumber = e164;
    this.phoneNumber = this.formatE164ToFriendly(e164);
  }

  async onClickConfirmPhoneNumber(): Promise<void> {
    if (!this.isPhoneFormatValid || !this.formattedSystemPhoneNumber) {
      this.verificationError = true;
      this.verifyErrorText = 'Enter a valid US mobile number.';
      return;
    }

    this.verificationError = false;
    this.verifyErrorText = '';
    this.isSuspectedVoip = false;
    this.isPhoneNumberUnique = true;
    this.isSendingCode = true;
    try {
      const isNumberUnique = await this.userService.isPhoneNumberUnique(this.formattedSystemPhoneNumber);
      if (!isNumberUnique) {
        this.isPhoneNumberUnique = false;
        this.verificationError = true;
        this.verifyErrorText = 'This mobile number is already associated with an account.';
        this.isSendingCode = false;
        return;
      }

      this.requestVerificationCode();
    } catch (error) {
      console.error('Error checking phone number:', error);
      this.verificationError = true;
      this.verifyErrorText = 'Unable to verify this mobile number. Please try again.';
      this.isSendingCode = false;
    }
  }

  onResendCode(): void {
    if (this.isSendingCode || this.resendCooldownSeconds > 0 || !this.formattedSystemPhoneNumber) {
      return;
    }

    this.verificationError = false;
    this.verifyErrorText = '';
    this.requestVerificationCode();
  }

  onCodeChange(event) {
    const verificationCode = event;
    if (verificationCode.length === 6) {
      this.verificationCode = verificationCode;
      this.isVerifyingCode = true;
      this.handleVerifyCode();
    }

  }

  handleVerifyCode() {
    this.verificationAttempts++;

    if (this.verificationAttempts > 10) {
      this.verificationError = true;
      this.verifyErrorText = 'Max attempts reached. Please try again later';
    }
    this.isVerifyingCode = true;
    this.userService.verifyCode(this.formattedSystemPhoneNumber, this.verificationCode)
      .subscribe(response => {
        console.log('Verification result:', response);
        if (response.isCodeVerified) {
          this.isVerifyingCode = false;
          this.isUpdatingUserAccount = true;
          this.handleVerificationCodeSuccess();
        } else {
          this.isVerifyingCode = false;
          this.verificationError = true;
          this.verifyErrorText = 'Invalid code. Check your code and try again.';
        }
      }, error => {
        console.error('Error verifying code:', error);
        this.verificationError = true;
        this.verifyErrorText = error?.error?.error || 'Unable to verify this mobile number. Please try again.';
        this.isVerifyingCode = false;
      });
  }

  handleVerificationCodeSuccess() {
    const numberForUniquenessCollection = this.formattedSystemPhoneNumber;
    const friendlyNumber = this.formatE164ToFriendly(this.formattedSystemPhoneNumber);
    this.userService.savePhoneNumberWithUserId(numberForUniquenessCollection, this.userId);
    const update = {
      mobileNumberFriendly: friendlyNumber,
      mobileNumber: this.formattedSystemPhoneNumber,
      isMobileVerified: true,
    }
    this.userService.updateUserProfile(update).then((data) => {
      this.isVerifyingCode = false;
      this.isPhoneVerified = true;
      this.isUpdatingUserAccount = false;
      this.router.navigate(['/onboard/account-type']);
    }).catch((e) => {
      console.log('error updating user profile', e);
      this.verificationError = true;
      this.isVerifyingCode = false;
    })
  }

  onRetryNumber() {
    this.phoneValue = null;
    this.phoneNumber = undefined;
    this.formattedSystemPhoneNumber = undefined;
    this.isPhoneFormatValid = false;
    this.isCodeSent = false;
    this.verificationError = false;
    this.verifyErrorText = '';
    this.verificationCode = undefined;
    this.verificationAttempts = 0;
    this.isPhoneNumberUnique = true;
    this.isSuspectedVoip = false;
    this.clearResendCooldown();
  }

  ngOnDestroy(): void {
    this.clearResendCooldown();
  }

  private requestVerificationCode(): void {
    this.isSendingCode = true;
    this.userService.sendVerificationCode(this.formattedSystemPhoneNumber!)
      .pipe(finalize(() => this.isSendingCode = false))
      .subscribe({
        next: response => {
          if (response.isCodeSent) {
            this.isCodeSent = true;
            this.startResendCooldown();
            return;
          }

          this.verificationError = true;
          this.verifyErrorText = 'Unable to send a verification code. Please try again.';
        },
        error: error => {
          if (error?.error?.isVoip) {
            this.voipAttemptCount++;
            this.userService.updateUserProfile({voipAttemptCount: this.voipAttemptCount});
            this.isSuspectedVoip = true;
          }
          console.error('Error sending code:', error);
          this.verificationError = true;
          this.verifyErrorText = error?.error?.error || 'Unable to verify. Make sure to use a valid US mobile number.';
        },
      });
  }

  private startResendCooldown(): void {
    this.clearResendCooldown();
    this.resendCooldownSeconds = 30;
    this.resendTimer = setInterval(() => {
      this.resendCooldownSeconds--;
      if (this.resendCooldownSeconds <= 0) {
        this.clearResendCooldown();
      }
    }, 1000);
  }

  private clearResendCooldown(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = undefined;
    }
    this.resendCooldownSeconds = 0;
  }

  formatE164ToFriendly(e164: string | null | undefined): string {
    if (!e164) return '';
    const digits = e164.replace(/\D/g, '');
    if (digits.length < 11) return e164;

    const country = digits[0]; // e.g. 1
    const area = digits.slice(1, 4);
    const exchange = digits.slice(4, 7);
    const subscriber = digits.slice(7, 11);

    return `+${country} (${area}) ${exchange}-${subscriber}`;
  }

}
